import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import moment from 'moment';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { postApi, patchApi, getApi } from '../services/api';

const validationSchema = Yup.object({
  title: Yup.string().required('Título es requerido').min(3, 'Mínimo 3 caracteres'),
  description: Yup.string(),
  status: Yup.string().oneOf(['open', 'in_progress', 'done', 'archived']).required('Estado es requerido'),
  priority: Yup.string().oneOf(['low', 'medium', 'high', 'urgent']).required('Prioridad es requerida'),
  tags: Yup.array().of(Yup.string()),
  assignees: Yup.array().of(Yup.string()),
  dueDate: Yup.date().nullable(),
  startDate: Yup.date().nullable(),
  checklist: Yup.array().of(
    Yup.object({
      text: Yup.string().required('Texto del item es requerido'),
      done: Yup.boolean()
    })
  )
});

const AddTaskDialog = ({ open, handleClose, onSuccess, editingTask = null }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Obtener datos del hotel del localStorage
  const hotel = JSON.parse(localStorage.getItem('hotelData')) || {};

  // Cargar empleados del hotel
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!hotel?.hotelId) return;

      try {
        const response = await getApi(`api/employee/viewallemployee/${hotel.hotelId}`);
        const employeesData = response?.data?.employeeData || [];
        setUsers(employeesData);
      } catch (error) {
        console.error('Error fetching employees:', error);
        // Fallback: try users if employees fail
        try {
          const userResponse = await getApi(`api/user?hotelId=${hotel.hotelId}`);
          const usersData = userResponse?.data?.data || [];
          setUsers(usersData);
        } catch (userError) {
          console.error('Error fetching users:', userError);
        }
      }
    };

    if (open) {
      fetchEmployees();
    }
  }, [open, hotel.hotelId]);

  const initialValues = editingTask
    ? {
        title: editingTask.title || '',
        description: editingTask.description || '',
        status: editingTask.status || 'open',
        priority: editingTask.priority || 'medium',
        tags: editingTask.tags || [],
        assignees: editingTask.assignees || [],
        watchers: editingTask.watchers || [],
        dueDate: editingTask.dueDate ? moment(editingTask.dueDate) : null,
        startDate: editingTask.startDate ? moment(editingTask.startDate) : null,
        checklist: editingTask.checklist || [],
        related: editingTask.related || {}
      }
    : {
        title: '',
        description: '',
        status: 'open',
        priority: 'medium',
        tags: [],
        assignees: [],
        watchers: [],
        dueDate: null,
        startDate: null,
        checklist: [],
        related: {}
      };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const taskData = {
        ...values,
        hotelId: hotel.hotelId,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
        startDate: values.startDate ? values.startDate.toISOString() : null,
        assignees: values.assignees.map((id) => id),
        watchers: values.watchers.map((id) => id),
        tags: values.tags
      };

      if (editingTask) {
        await patchApi(`api/tasks/${editingTask._id}`, taskData);
      } else {
        await postApi('api/tasks', taskData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving task:', error);
      // Handle error (could add toast notification)
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const addChecklistItem = (setFieldValue, currentChecklist) => {
    const newItem = { text: '', done: false };
    setFieldValue('checklist', [...currentChecklist, newItem]);
  };

  const removeChecklistItem = (setFieldValue, currentChecklist, index) => {
    const updatedChecklist = currentChecklist.filter((_, i) => i !== index);
    setFieldValue('checklist', updatedChecklist);
  };

  const toggleChecklistItem = (setFieldValue, currentChecklist, index) => {
    const updatedChecklist = [...currentChecklist];
    updatedChecklist[index].done = !updatedChecklist[index].done;
    setFieldValue('checklist', updatedChecklist);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogTitle>{editingTask ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>

              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Título y Descripción */}
                  <Grid item xs={12}>
                    <Field name="title">
                      {({ field }) => (
                        <TextField
                          {...field}
                          label="Título *"
                          fullWidth
                          error={touched.title && Boolean(errors.title)}
                          helperText={touched.title && errors.title}
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <Field name="description">
                      {({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          fullWidth
                          multiline
                          rows={3}
                          error={touched.description && Boolean(errors.description)}
                          helperText={touched.description && errors.description}
                        />
                      )}
                    </Field>
                  </Grid>

                  {/* Estado y Prioridad */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={touched.status && Boolean(errors.status)}>
                      <InputLabel>Estado *</InputLabel>
                      <Field name="status">
                        {({ field }) => (
                          <Select {...field} label="Estado *">
                            <MenuItem value="open">Abierta</MenuItem>
                            <MenuItem value="in_progress">En Progreso</MenuItem>
                            <MenuItem value="done">Completada</MenuItem>
                            <MenuItem value="archived">Archivada</MenuItem>
                          </Select>
                        )}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={touched.priority && Boolean(errors.priority)}>
                      <InputLabel>Prioridad *</InputLabel>
                      <Field name="priority">
                        {({ field }) => (
                          <Select {...field} label="Prioridad *">
                            <MenuItem value="low">Baja</MenuItem>
                            <MenuItem value="medium">Media</MenuItem>
                            <MenuItem value="high">Alta</MenuItem>
                            <MenuItem value="urgent">Urgente</MenuItem>
                          </Select>
                        )}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Fechas */}
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Fecha de Inicio"
                      value={values.startDate}
                      onChange={(date) => setFieldValue('startDate', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Fecha Límite"
                      value={values.dueDate}
                      onChange={(date) => setFieldValue('dueDate', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>

                  {/* Asignados y Observadores */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                      value={users.filter((user) => values.assignees.includes(user._id))}
                      onChange={(event, newValue) => {
                        setFieldValue(
                          'assignees',
                          newValue.map((user) => user._id)
                        );
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            key={option._id || index}
                            label={`${option.firstName} ${option.lastName}`}
                            {...getTagProps({ index })}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => <TextField {...params} label="Asignados" placeholder="Seleccionar usuarios" />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                      value={users.filter((user) => values.watchers.includes(user._id))}
                      onChange={(event, newValue) => {
                        setFieldValue(
                          'watchers',
                          newValue.map((user) => user._id)
                        );
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            key={option._id || index}
                            label={`${option.firstName} ${option.lastName}`}
                            {...getTagProps({ index })}
                            size="small"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => <TextField {...params} label="Observadores" placeholder="Seleccionar usuarios" />}
                    />
                  </Grid>

                  {/* Etiquetas */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]} // Could be populated with common tags
                      value={values.tags}
                      onChange={(event, newValue) => {
                        setFieldValue('tags', newValue);
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            key={option || index}
                            label={option}
                            {...getTagProps({ index })}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => <TextField {...params} label="Etiquetas" placeholder="Agregar etiquetas" />}
                    />
                  </Grid>

                  {/* Checklist */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">Checklist</Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => addChecklistItem(setFieldValue, values.checklist)}
                        size="small"
                        variant="outlined"
                      >
                        Agregar Item
                      </Button>
                    </Box>

                    <Stack spacing={1}>
                      {values.checklist.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => toggleChecklistItem(setFieldValue, values.checklist, index)}>
                            {item.done ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon />}
                          </IconButton>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.text}
                            onChange={(e) => {
                              const updatedChecklist = [...values.checklist];
                              updatedChecklist[index].text = e.target.value;
                              setFieldValue('checklist', updatedChecklist);
                            }}
                            placeholder="Descripción del item"
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeChecklistItem(setFieldValue, values.checklist, index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting || loading}>
                  Cancelar
                </Button>
                <Button type="submit" variant="contained" disabled={isSubmitting || loading}>
                  {loading ? 'Guardando...' : editingTask ? 'Actualizar' : 'Crear'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddTaskDialog;
